import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SelectField from "components/SelectField";
import TextField from "components/TextField";

import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
// import { useState } from "react";
import { ReactComponent as LoveIcon } from "assets/icons/love.svg";
import { ReactComponent as SadIcon } from "assets/icons/SAD.svg";
import CloseIcon from "@mui/icons-material/Close";
import { sendFeedback } from "_services";

import { useSnackbar } from "notistack";

const Feedback = ({ onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const schema = yup.object().shape({
    satisfied: yup.string().required(t("page_feedback.helpText_are_you_satisfied")).nullable(),
    typeFeedback: yup.string(),
    feedback: yup.string(),
  });

  const { handleSubmit, control, formState: { isSubmitting } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      satisfied: null,
      typeFeedback: 1,
      feedback: "",
    },
  });

  const onSubmit = async (data) => {
    try {
      await sendFeedback({
        satisfied: data.satisfied,
        typeFeedback: data.typeFeedback,
        feedback: data.feedback,
      });
      onClose();
      enqueueSnackbar("", {
        content: (key) => (
          <Alert severity="success" id={key}>
            {t("dialog.sendsuccess")}
          </Alert>
        ),
      });
    } catch (e) {
      enqueueSnackbar("", {
        content: (key) => (
          <Alert severity="error" id={key}>
            {t("dialog.cannot_process")}
          </Alert>
        ),
      });
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 752,
        width: "100%",
        mx: "auto",
        mt: "51px",
        whiteSpace: "pre-line",
        p: "16px",
        boxSizing: "border-box",
      }}
    >
      <Stack gap="16px" alignItems="flex-start">
        {/* title */}
        <Box sx={{ width: "100%" }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h4" color="primary.main">
              {t("page_feedback.title")}
            </Typography>
            <CloseIcon
              onClick={onClose}
              sx={{ fontSize: "48px", color: "#BDBDBD", cursor: "pointer" }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: "12px" }}>
            {t("page_feedback.subtitle")}
          </Typography>
        </Box>
        {/* satisfied */}
        <Stack gap="16px">
          <Typography variant="h6">{t("page_feedback.are_you_satisfied")}*</Typography>
          <Controller
            name="satisfied"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <div>
                <RadioGroup row value={value} onChange={(event) => onChange(event.target.value)}>
                  <Stack direction="row" gap="18px">
                    <FormControlLabel
                      value={1}
                      control={<Radio />}
                      label={
                        <Stack direction="row" alignItems="center" spacing="4px">
                          <Typography variant="body1">{t("page_feedback.satisfied")}</Typography>
                          <LoveIcon />
                        </Stack>
                      }
                    ></FormControlLabel>
                    <FormControlLabel
                      value={0}
                      control={<Radio />}
                      label={
                        <Stack direction="row" alignItems="center" spacing="4px">
                          <Typography variant="body1">{t("page_feedback.unsatisfied")}</Typography>
                          <SadIcon />
                        </Stack>
                      }
                    ></FormControlLabel>
                  </Stack>
                </RadioGroup>

                {!!error && (
                  <Box color="error.main" fontSize="0.75rem">
                    {error.message}
                  </Box>
                )}
              </div>
            )}
          />
        </Stack>
        {/* type feedback */}
        <Box sx={{ maxWidth: "320px", width: "100%" }}>
          <Controller
            control={control}
            name="typeFeedback"
            render={({ field: { onChange, value } }) => (
              <>
                <SelectField
                  label={t("page_feedback.feedback_type")}
                  items={[
                    {
                      id: 1,
                      value: t("page_feedback.general_feedback"),
                    },
                    {
                      id: 2,
                      value: t("page_feedback.desired_function"),
                    },
                    {
                      id: 3,
                      value: t("page_feedback.bugs_or_performance_issues"),
                    },
                  ]}
                  value={value}
                  onChange={(event) => onChange(event.target.value)}
                />
              </>
            )}
          />
        </Box>
        {/* type the text below */}
        <Box sx={{ width: "100%" }}>
          <Controller
            control={control}
            name="feedback"
            render={({ field: { onChange, value } }) => (
              <TextField
                label={t("page_feedback.type_the_text_below")}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                multiline
                inputProps={{ maxLength: 1000 }}
                rows={3}
                sx={{
                  "& .MuiInputBase-root.MuiFilledInput-root": {
                    p: "8px 12px",
                  },
                }}
              />
            )}
          />
        </Box>
        {/* contact us */}
        <Typography variant="body2" color="text.secondary">
          {t("page_feedback.or_contact_us")}
        </Typography>
        {/* button */}
        <Stack direction="row" justifyContent="space-between" sx={{ width: "100%" }}>
          <Button
            variant="standard"
            color="primary"
            onClick={onClose}
            sx={{ minWidth: "150px" }}
            size="large"
          >
            {t("back")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            sx={{ minWidth: "150px" }}
            size="large"
            disabled={isSubmitting}
          >
            {t("page_feedback.send_you_a_questionnaire")}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default Feedback;
